/**
 * Selects the document-specific clinical summary body for a patient.
 */

import { FileText } from 'lucide-react';
import type { DocumentType, Patient } from '../../../types';
import { ECOGSummary } from '../ECOG/ECOGSummary';
import { LabResultsSummary } from '../LabResults/LabResultsSummary';
import { MDVisitNoteSummary } from '../MDVisitNote/MDVisitNoteSummary';
import { MolecularResultsSummary } from '../MolecularResults/MolecularResultsSummary';
import { PathologyResultsSummary } from '../PathologyResults/PathologyResultsSummary';
import { PerDocumentDiffView } from '../PerDocument/PerDocumentDiffView';
import { PrescriptionsSummary } from '../Prescriptions/PrescriptionsSummary';
import { RadiologySummary } from '../Radiology/RadiologySummary';

interface DocumentSummaryContentProps {
  patient: Patient;
  documentType: DocumentType;
  isLoading?: boolean;
}

// Top-level extraction keys each structured view (plus the shared diagnosis band) already
// renders. They are excluded from the "Additional Details" panel so it shows ONLY the
// cancer-specific extras. Keep in sync with the backend summary_shaper `_shape_*` functions
// (and their alias source keys). Erring toward under-exclusion is safe: a missed key merely
// duplicates one value, whereas over-excluding would hide cancer-specific data.
const BAND_KEYS = [
  'patient_identity',
  'stage',
  'tnm',
  'histology',
  'histologic_type',
  'primary_diagnosis',
  'diagnosis',
  'metastatic_sites',
  'disease_status',
  'line_of_therapy',
  'ecog_status',
  'diagnosed_on',
];

const STRUCTURED_VIEW_KEYS: Partial<Record<DocumentType, string[]>> = {
  Radiology: [
    ...BAND_KEYS,
    'study_type',
    'study_date',
    'clinical_indication',
    'indication',
    'overall_response',
    'response_criteria',
    'prior_comparison_study',
    'sum_percentage_change_from_baseline',
    'target_lesions',
    'lymph_nodes',
    'new_lesions',
    'urgent_findings',
    'impression',
  ],
  'MD Visit Note': [
    ...BAND_KEYS,
    'provider',
    'treatment_lines',
    'treatment_timeline',
    'key_comorbidities',
    'other_comorbidities',
    'current_medications',
  ],
  'Molecular Results': [
    ...BAND_KEYS,
    'test_platform',
    'sample_type',
    'report_date',
    'sample_collection_date',
    'alterations',
    'ngs_mutations',
    'fish_rearrangements',
    'targetable_markers',
    'pdl1_tps',
    'pdl1_cps',
    'pdl1_assay',
    'tmb_score',
    'tmb_classification',
    'msi_status',
    'mmr_status',
    'er_status',
    'pr_status',
    'her2_status',
    'ki67',
  ],
  'Pathology Results': [
    ...BAND_KEYS,
    'pathologic_tnm_stage',
    'pathologic_stage',
    'grade',
    'nottingham_grade',
    'differentiation_grade',
    'who_grade',
    'diagnosis_narrative',
    'specimen_site',
    'biopsy_site',
    'histologic_subtype',
    'tumor_size_cm',
    'lymphovascular_invasion',
    'perineural_invasion',
    'pleural_invasion',
    'extranodal_extension',
    'nodes_examined',
    'nodes_positive',
    'treatment_effect',
    'pathologic_complete_response',
    'margin_status',
    'laterality',
    'specimen_type',
  ],
  ECOG: [
    ...BAND_KEYS,
    'assessment_date',
    'ecog_score',
    'ecog_patient_recorded',
    'ecog_score_pro',
    'ecog_score_patient_recorded',
    'score_label',
    'source',
    'assessed_by',
    'clinical_notes',
    'trend',
    'trend_vs_prior',
    'assessing_physician',
    'patient_reported_description',
    'total_assessments_on_record',
  ],
  Prescriptions: [
    'patient_identity',
    'script_date',
    'prescriber',
    'drug',
    'instructions',
    'generic',
    'dispense_qty',
    'dispense_qty_text',
    'refills',
    'daw',
    'note',
  ],
  'Lab Results': ['patient_identity'],
};

/**
 * Panel shown for a NON-cancer patient (no malignant-neoplasm 'C' code in the latest MD
 * note). Smart summarization is cancer-eligibility focused, so it's skipped — the CRC can
 * still open the source document via "View Source" (top right).
 *
 * @returns The "summary skipped" panel
 */
function SummarySkippedPanel() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '0.5rem',
        padding: '3rem 1.5rem',
        margin: '1rem 0',
        border: '1px dashed var(--color-border)',
        borderRadius: '0.5rem',
        backgroundColor: 'var(--color-card)',
      }}
    >
      <FileText style={{ width: 28, height: 28, color: 'var(--color-text-muted)' }} />
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
        Smart summary not generated
      </h3>
      <p style={{ margin: 0, maxWidth: 520, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        There are no cancer ICD codes for this patient in the latest MD notes, so smart summary generation was
        skipped. You can view the source document via <strong>View Source</strong> (top right) to preview it.
      </p>
    </div>
  );
}

/**
 * Determine whether the selected document type has its typed API payload.
 *
 * @param patient - Hydrated patient summary.
 * @param documentType - Selected document type.
 * @returns Whether a dedicated summary component has data to render.
 */
function hasTypedSummary(patient: Patient, documentType: DocumentType): boolean {
  switch (documentType) {
    case 'Lab Results':
      return Boolean(patient.labResults);
    case 'Radiology':
      return Boolean(patient.radiology);
    case 'Molecular Results':
      return Boolean(patient.molecular);
    case 'Pathology Results':
      return Boolean(patient.pathologyReport);
    case 'MD Visit Note':
      return Boolean(patient.mdVisitNote);
    case 'Prescriptions':
      return Boolean(patient.prescriptions);
    case 'ECOG':
      return Boolean(patient.ecog);
    default:
      return false;
  }
}

/**
 * Render a typed reference layout and preserve the generic extraction fallback.
 *
 * @param props - Patient, selected document type, and loading state.
 * @returns The selected document summary body.
 */
export function DocumentSummaryContent({
  patient,
  documentType,
  isLoading,
}: DocumentSummaryContentProps) {
  // Non-cancer patients get no smart summary (skipped at ingest) — show the panel + View Source.
  if (!isLoading && !patient.isCancer) {
    return <SummarySkippedPanel />;
  }

  if (isLoading || !hasTypedSummary(patient, documentType)) {
    return (
      <PerDocumentDiffView
        documents={patient.documents}
        narrative={patient.narrative}
        isLoading={isLoading}
      />
    );
  }

  // Structured view (curated, consistent fields) + an "Additional Details" panel that surfaces
  // any remaining non-null raw fields the structured view doesn't render — i.e. the cancer-
  // specific extras (Gleason, Ann Arbor/R-ISS, heme FISH/somatic mutations, ECOG pain/HRQoL, …).
  // Uses data already in the payload (`patient.documents`), so no re-summarization is needed.
  return (
    <>
      {renderStructuredSummary(patient, documentType)}
      <PerDocumentDiffView
        documents={patient.documents}
        excludeKeys={STRUCTURED_VIEW_KEYS[documentType]}
        hideNarrative
        title="Additional Details"
      />
    </>
  );
}

/**
 * Render the per-type structured (curated) summary component.
 *
 * @param patient - Hydrated patient
 * @param documentType - Active document tab
 * @returns The structured summary element, or null for an unknown type
 */
function renderStructuredSummary(patient: Patient, documentType: DocumentType) {
  switch (documentType) {
    case 'Lab Results':
      return <LabResultsSummary patient={patient} />;
    case 'Radiology':
      return <RadiologySummary patient={patient} />;
    case 'Molecular Results':
      return <MolecularResultsSummary patient={patient} />;
    case 'Pathology Results':
      return <PathologyResultsSummary patient={patient} />;
    case 'MD Visit Note':
      return <MDVisitNoteSummary patient={patient} />;
    case 'Prescriptions':
      return <PrescriptionsSummary patient={patient} />;
    case 'ECOG':
      return <ECOGSummary patient={patient} />;
    default:
      return null;
  }
}

export default DocumentSummaryContent;
